import {
  buildPostHogErrorMetadata,
  normalizePostHogStackFilename,
  redactPostHogErrorMessage,
  sanitizePostHogErrorProperties,
} from './posthog-error-policy';
import type { BeforeSendFn, CaptureResult, PostHogConfig } from 'posthog-js';

type UnknownRecord = Record<string, unknown>;

export type PostHogExceptionEvent = {
  event: string;
  properties: UnknownRecord;
};

export const POSTHOG_BROWSER_ERROR_CONFIG = {
  capture_exceptions: {
    capture_console_errors: false,
    capture_unhandled_errors: true,
    capture_unhandled_rejections: true,
  },
  error_tracking: {
    captureExtensionExceptions: false,
  },
  mask_all_text: true,
  session_recording: {
    blockClass: 'ph-no-capture',
    maskAllInputs: true,
    maskTextClass: 'ph-mask',
    recordBody: false,
    recordHeaders: false,
  },
} satisfies Partial<PostHogConfig>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function exceptionList(properties: UnknownRecord): UnknownRecord[] {
  const value = properties.$exception_list;
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function exceptionMessage(properties: UnknownRecord): string {
  const first = exceptionList(properties)[0];
  if (typeof first?.value === 'string') return first.value;
  const values = properties.$exception_values;
  if (Array.isArray(values)) {
    const message = values.find((value) => typeof value === 'string');
    if (typeof message === 'string') return message;
  }
  return 'Unknown error';
}

function exceptionName(properties: UnknownRecord): string | undefined {
  const first = exceptionList(properties)[0];
  return typeof first?.type === 'string' ? first.type : undefined;
}

function exceptionHandled(properties: UnknownRecord): boolean {
  const first = exceptionList(properties)[0];
  const mechanism = first?.mechanism;
  return isRecord(mechanism) && typeof mechanism.handled === 'boolean'
    ? mechanism.handled
    : false;
}

function exceptionTopFrame(properties: UnknownRecord): string | undefined {
  for (const exception of exceptionList(properties)) {
    if (!isRecord(exception.stacktrace)) continue;
    const frames = exception.stacktrace.frames;
    if (!Array.isArray(frames)) continue;
    const records = frames.filter(isRecord);
    const frame =
      [...records].reverse().find((candidate) => candidate.in_app === true) ??
      records.at(-1);
    const filename = frame?.filename ?? frame?.abs_path;
    if (typeof filename === 'string') {
      return normalizePostHogStackFilename(filename);
    }
  }
  return undefined;
}

function exceptionOperation(properties: UnknownRecord): string | undefined {
  for (const key of [
    'error_operation',
    'operation',
    'contextEvent',
    '$exception_function',
  ]) {
    const value = properties[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return undefined;
}

const explicitErrorCode = (properties: UnknownRecord): string | undefined => {
  const value = properties.error_code;
  return typeof value === 'string' && /^[A-Z][A-Z0-9_]{2,100}$/.test(value)
    ? value
    : undefined;
};

function sanitizeExceptionFrame(frame: UnknownRecord): UnknownRecord {
  const sanitized: UnknownRecord = {};
  for (const key of [
    'abs_path',
    'filename',
    'function',
    'module',
    'platform',
  ]) {
    const value = frame[key];
    if (typeof value === 'string') {
      sanitized[key] =
        key === 'abs_path' || key === 'filename'
          ? normalizePostHogStackFilename(value)
          : redactPostHogErrorMessage(value);
    }
  }
  for (const key of ['colno', 'lineno']) {
    const value = frame[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      sanitized[key] = value;
    }
  }
  if (typeof frame.in_app === 'boolean') sanitized.in_app = frame.in_app;
  return sanitized;
}

function sanitizeExceptionList(properties: UnknownRecord): UnknownRecord[] {
  return exceptionList(properties).map((exception) => {
    const sanitized: UnknownRecord = {};
    if (typeof exception.type === 'string') sanitized.type = exception.type;
    if (typeof exception.value === 'string') {
      sanitized.value = redactPostHogErrorMessage(exception.value);
    }
    if (isRecord(exception.mechanism)) {
      sanitized.mechanism = {
        ...(typeof exception.mechanism.handled === 'boolean'
          ? { handled: exception.mechanism.handled }
          : {}),
        ...(typeof exception.mechanism.synthetic === 'boolean'
          ? { synthetic: exception.mechanism.synthetic }
          : {}),
        ...(typeof exception.mechanism.type === 'string'
          ? { type: exception.mechanism.type }
          : {}),
      };
    }
    if (
      isRecord(exception.stacktrace) &&
      Array.isArray(exception.stacktrace.frames)
    ) {
      sanitized.stacktrace = {
        frames: exception.stacktrace.frames
          .filter(isRecord)
          .slice(-50)
          .map(sanitizeExceptionFrame),
      };
    }
    return sanitized;
  });
}

export function transformPostHogExceptionEvent<T extends PostHogExceptionEvent>(
  event: T | null
): T | null {
  if (!event || event.event !== '$exception') return event;

  const original = event.properties;
  const sanitized = sanitizePostHogErrorProperties(
    original,
    0,
    Number.POSITIVE_INFINITY
  );
  const projectToken =
    typeof original.token === 'string' ? original.token : undefined;
  const sanitizedList = sanitizeExceptionList(original);
  const metadata = buildPostHogErrorMetadata({
    errorCode: explicitErrorCode(original),
    errorName: exceptionName(original),
    handled: exceptionHandled(original),
    message: exceptionMessage(original),
    operation: exceptionOperation(original),
    topApplicationFrame: exceptionTopFrame(original),
  });

  return {
    ...event,
    properties: {
      ...sanitized,
      ...(projectToken ? { token: projectToken } : {}),
      ...(sanitizedList.length > 0 ? { $exception_list: sanitizedList } : {}),
      ...metadata,
    },
  };
}

export function createPostHogExceptionRateLimiter(args?: {
  maxPerWindow?: number;
  now?: () => number;
  windowMs?: number;
}): (event: PostHogExceptionEvent | null) => PostHogExceptionEvent | null {
  const maxPerWindow = args?.maxPerWindow ?? 3;
  const now = args?.now ?? Date.now;
  const windowMs = args?.windowMs ?? 60_000;
  const buckets = new Map<
    string,
    { count: number; startedAt: number; suppressed: number }
  >();

  return (event) => {
    if (!event || event.event !== '$exception') return event;
    const fingerprint = event.properties.$exception_fingerprint;
    if (typeof fingerprint !== 'string') return event;

    const timestamp = now();
    const bucket = buckets.get(fingerprint);
    if (!bucket || timestamp - bucket.startedAt >= windowMs) {
      const suppressed = bucket?.suppressed ?? 0;
      buckets.set(fingerprint, {
        count: 1,
        startedAt: timestamp,
        suppressed: 0,
      });
      return suppressed > 0
        ? {
            ...event,
            properties: {
              ...event.properties,
              error_suppressed_repeats: suppressed,
            },
          }
        : event;
    }

    bucket.count += 1;
    if (bucket.count > maxPerWindow) {
      bucket.suppressed += 1;
      return null;
    }
    return event;
  };
}

export function createPostHogExceptionBeforeSend(): BeforeSendFn[] {
  const rateLimit = createPostHogExceptionRateLimiter();
  return [
    (event) => transformPostHogExceptionEvent(event),
    (event) => rateLimit(event) as CaptureResult | null,
  ];
}
